import { CheckIcon } from 'lucide-react'

import { Link } from '@/components/ui'
import type { Project } from '@/modules/projects'
import { cn } from '@/utils'
import type { GettingStartedStep as GettingStartedStepData } from '../steps'
import { StepAction } from './StepAction'
import { StepClip } from './StepClip'

interface GettingStartedStepProps {
  step: GettingStartedStepData
  number: number
  done: boolean
  isNext: boolean
  project: Project | null
}

export function GettingStartedStep({
  step,
  number,
  done,
  isNext,
  project,
}: GettingStartedStepProps) {
  return (
    <li
      className={cn(
        'grid gap-6 rounded-2xl border bg-white p-5 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] md:items-center md:gap-10 md:p-8',
        isNext && 'border-brand/50 shadow-md',
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
              done ? 'bg-brand text-white' : 'bg-blue-2 text-blue-7',
            )}
          >
            {done ? <CheckIcon className="size-4" aria-label="Done" /> : number}
          </span>
          {isNext && (
            <span className="text-xs font-semibold uppercase tracking-wide text-brand-deep">
              Up next
            </span>
          )}
        </div>
        <h2 className="text-pretty text-xl font-semibold leading-snug text-blue-7">
          {step.title}
        </h2>
        <p className="text-pretty text-base leading-relaxed text-blue-6">
          {step.description}
        </p>
        {step.upgradeNote && (
          <p className="text-pretty text-sm text-blue-5">
            {step.upgradeNote}{' '}
            <Link to="/billing" theme="brand" size="sm">
              See your plan
            </Link>
          </p>
        )}
        {step.action && (
          <div className="pt-1">
            <StepAction
              action={step.action}
              project={project}
              theme={isNext ? 'default' : 'outline'}
            />
          </div>
        )}
      </div>
      <StepClip clip={step.clip} title={step.title} />
    </li>
  )
}
